import { store } from '../store/store'
import { setMaintenanceMode } from '../store/appSlice'

const injectSource = (payload: any) => {
  if (payload instanceof FormData) {
    if (!payload.has('source') && !payload.has('Source')) {
      payload.append('source', 'Desktop')
    }
    return payload
  }

  const alreadyHasSource =
    payload &&
    (Object.prototype.hasOwnProperty.call(payload, 'source') ||
      Object.prototype.hasOwnProperty.call(payload, 'Source'))

  if (alreadyHasSource) {
    return payload
  }

  return {
    source: 'Desktop',
    ...(payload || {}),
  }
}

const getFullUrl = (endpoint: string): string => {
  const baseUrl: string = import.meta.env.VITE_GOWARDHAN_ERP_BASE_URL || ''
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  return `${cleanBase}${cleanEndpoint}`
}

const handleTokenExpiration = () => {
  localStorage.removeItem('gowardhan_erp_token')
  localStorage.removeItem('gowardhan_erp_session')
  localStorage.removeItem('gowardhan_erp_profile')

  const baseUrl = import.meta.env.VITE_BUILD_URL || ''
  const loginPath = baseUrl ? (baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`) : '/'
  const currentPath = window.location.pathname

  if (
    currentPath !== loginPath &&
    currentPath !== '/' &&
    currentPath !== loginPath.slice(0, -1) &&
    !currentPath.endsWith('/login')
  ) {
    window.location.href = loginPath
  }
}

const handle403Forbidden = () => {
  const baseUrl = import.meta.env.VITE_BUILD_URL || ''
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const forbiddenPath = cleanBase ? `${cleanBase}/403` : '/403'
  const currentPath = window.location.pathname

  if (!currentPath.endsWith('/403')) {
    window.location.href = forbiddenPath
  }
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 503 || response.status === 502) {
    let msg = 'System is under maintenance. Please try again later.'
    try {
      const errJson = await response.json()
      if (errJson?.message) msg = errJson.message
    } catch {
      // fallback
    }
    store.dispatch(setMaintenanceMode({ isMaintenance: true, message: msg }))
    throw new Error(msg)
  }

  if (response.status === 401) {
    handleTokenExpiration()
    throw new Error('Token is invalid or expired')
  } else if (response.status === 403) {
    console.log("response status", response.status)
    handle403Forbidden()
    throw new Error('Access forbidden (403): You do not have permission to access this resource')
  }

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`
    try {
      const errorData = await response.json()
      errorMessage = errorData.message || errorMessage
    } catch {
      try {
        const text = await response.text()
        if (text) errorMessage = text
      } catch {
        // Ignore fallback text reading error
      }
    }

    const lowerMsg = errorMessage.toLowerCase()
    if (
      lowerMsg.includes('token is invalid') ||
      lowerMsg.includes('token is expired') ||
      lowerMsg.includes('invalid token') ||
      lowerMsg.includes('jwt expired') ||
      lowerMsg.includes('unauthorized')
    ) {
      handleTokenExpiration()
    }

    throw new Error(errorMessage)
  }
  return (await response.json()) as T
}

// postAPI with token (supports JSON & FormData)
export const postAPIwithToken = async <T = any>(
  endpoint: string,
  payload?: any
): Promise<T> => {
  const url: string = getFullUrl(endpoint)
  const token: string | null = localStorage.getItem('gowardhan_erp_token')

  try {
    const injected = injectSource(payload)
    const isFormData = injected instanceof FormData
    const body = isFormData ? injected : JSON.stringify(injected)

    const headers: Record<string, string> = {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
    })

    return await handleResponse<T>(response)
  } catch (error) {
    console.error('API call error:', error)
    throw error
  }
}

// postAPI without token
export const postAPIwithoutToken = async <T = any>(
  endpoint: string,
  payload?: any
): Promise<T> => {
  const url: string = getFullUrl(endpoint)

  try {
    const injected = injectSource(payload)
    const isFormData = injected instanceof FormData
    const body = isFormData ? injected : JSON.stringify(injected)

    const headers: Record<string, string> = {}
    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
    })

    return await handleResponse<T>(response)
  } catch (error) {
    console.error('API call error:', error)
    throw error
  }
}

// getAPI with token
export const getAPIwithToken = async <T = any>(
  endpoint: string,
): Promise<T> => {
  const url: string = getFullUrl(endpoint)
  const token: string | null = localStorage.getItem('gowardhan_erp_token')

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'ngrok-skip-browser-warning': 'true',
      },
    })

    return await handleResponse<T>(response)
  } catch (error) {
    console.error('API call error:', error)
    throw error
  }
}

// patchAPI with token
export const patchAPIwithToken = async <T = any>(
  endpoint: string,
  payload?: any
): Promise<T> => {
  const url: string = getFullUrl(endpoint)
  const token: string | null = localStorage.getItem('gowardhan_erp_token')

  try {
    const injected = injectSource(payload)
    const isFormData = injected instanceof FormData
    const body = isFormData ? injected : JSON.stringify(injected)

    const headers: Record<string, string> = {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body,
    })

    return await handleResponse<T>(response)
  } catch (error) {
    console.error('API call error:', error)
    throw error
  }
}

// deleteAPI with token
export const deleteAPIwithToken = async <T = any>(
  endpoint: string,
  payload?: any
): Promise<T> => {
  const url: string = getFullUrl(endpoint)
  const token: string | null = localStorage.getItem('gowardhan_erp_token')

  try {
    const injected = payload ? injectSource(payload) : undefined
    const isFormData = injected instanceof FormData
    const body = injected ? (isFormData ? injected : JSON.stringify(injected)) : undefined

    const headers: Record<string, string> = {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    }
    if (body && !isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body,
    })

    return await handleResponse<T>(response)
  } catch (error) {
    console.error('API call error:', error)
    throw error
  }
}

// putAPI with token
export const putAPIwithToken = async <T = any>(
  endpoint: string,
  payload?: any
): Promise<T> => {
  const url: string = getFullUrl(endpoint)
  const token: string | null = localStorage.getItem('gowardhan_erp_token')

  try {
    const injected = injectSource(payload)
    const isFormData = injected instanceof FormData
    const body = isFormData ? injected : JSON.stringify(injected)

    const headers: Record<string, string> = {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    }
    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body,
    })

    return await handleResponse<T>(response)
  } catch (error) {
    console.error('API call error:', error)
    throw error
  }
}
